import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  CardActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Alert,
  CircularProgress,
  IconButton,
  Chip,
  FormControl,
  InputLabel,
  Select,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  Checkbox,
  Grid,
  InputAdornment,
  Collapse
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  AccountBalance as AccountIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  Refresh as RefreshIcon,
  ViewModule as ViewModuleIcon,
  ViewList as ViewListIcon,
  FilterList as FilterIcon,
  Search as SearchIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { api } from '../contexts/ApiContext';
import { useAuth } from '../contexts/AuthContext';
import { filterBankAccountsForSeanViewer } from '../utils/seanViewer';
import {
  FIT_TABLE_SX,
  FIT_TABLE_CONTAINER_SX,
  FitColgroup,
  buildFitColgroup,
  fitTruncateSx,
  fitChipSx,
  fitHeaderLabel,
} from '../utils/fitTable';

interface BankAccount {
  id: number;
  account_name: string;
  account_number: string;
  bank_name: string;
  account_type: string;
  current_balance: number;
  created_at: string;
  updated_at: string;
}

export const BankAccounts: React.FC = () => {
  const { user } = useAuth();
  const [accounts, setAccounts] = useState<BankAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState<BankAccount | null>(null);
  const [formData, setFormData] = useState({
    account_name: '',
    account_number: '',
    bank_name: '',
    account_type: 'business',
    current_balance: ''
  });
  const [viewMode, setViewMode] = useState<'table' | 'tile'>('table');
  const [selectedAccounts, setSelectedAccounts] = useState<number[]>([]);
  const [showFilters, setShowFilters] = useState(false);
  
  // Filter states
  const [accountNameFilter, setAccountNameFilter] = useState<string>('');
  const [bankNameFilter, setBankNameFilter] = useState<string>('');
  const [accountTypeFilter, setAccountTypeFilter] = useState<string>('');

  const fetchAccounts = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await api.get('/api/accounts/');
      setAccounts(response.data.accounts || []);
    } catch (err: any) {
      console.error('Error fetching accounts:', err);
      setError(err.response?.data?.error || 'Failed to load bank accounts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAccounts();
  }, []);

  // Filter accounts based on filter states. Sean only sees manual Rev Biz Ire/UK (ids 1 and 2).
  const viewerAccounts = filterBankAccountsForSeanViewer(accounts, user?.email);
  const filteredAccounts = viewerAccounts.filter(account => {
    // Account name filter (case-insensitive search)
    if (accountNameFilter && !account.account_name?.toLowerCase().includes(accountNameFilter.toLowerCase())) {
      return false;
    }
    
    // Bank name filter (case-insensitive search)
    if (bankNameFilter && !account.bank_name?.toLowerCase().includes(bankNameFilter.toLowerCase())) {
      return false;
    }
    
    // Account type filter
    if (accountTypeFilter && account.account_type !== accountTypeFilter) {
      return false;
    }
    
    return true;
  });

  const handleOpenDialog = (account?: BankAccount) => {
    if (account) {
      setSelectedAccount(account);
      setFormData({
        account_name: account.account_name,
        account_number: account.account_number,
        bank_name: account.bank_name,
        account_type: account.account_type,
        current_balance: account.current_balance.toString()
      });
    } else {
      setSelectedAccount(null);
      setFormData({
        account_name: '',
        account_number: '',
        bank_name: '',
        account_type: 'business',
        current_balance: ''
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedAccount(null);
    setError('');
  };

  const handleOpenDeleteDialog = (account: BankAccount) => {
    setSelectedAccount(account);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setSelectedAccount(null);
  };

  const handleSubmit = async () => {
    setError('');
    try {
      const payload = {
        ...formData,
        current_balance: parseFloat(formData.current_balance) || 0
      };

      if (selectedAccount) {
        await api.put(`/api/accounts/${selectedAccount.id}`, payload);
      } else {
        await api.post('/api/accounts/', payload);
      }
      
      handleCloseDialog();
      fetchAccounts();
    } catch (err: any) {
      console.error('Failed to save account:', err);
      setError(err.response?.data?.error || 'Failed to save bank account');
    }
  };

  const handleDelete = async () => {
    if (selectedAccount) {
      try {
        await api.delete(`/api/accounts/${selectedAccount.id}`);
        handleCloseDeleteDialog();
        fetchAccounts();
      } catch (err: any) {
        console.error('Failed to delete account:', err);
        setError(err.response?.data?.error || 'Failed to delete bank account');
      }
    }
  };

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelectedAccounts(filteredAccounts.map(a => a.id));
    } else {
      setSelectedAccounts([]);
    }
  };

  const handleSelectOne = (accountId: number) => {
    setSelectedAccounts(prev => {
      if (prev.includes(accountId)) {
        return prev.filter(id => id !== accountId);
      } else {
        return [...prev, accountId];
      }
    });
  };

  const handleBulkDelete = async () => {
    if (selectedAccounts.length === 0) return;
    
    if (!window.confirm(`Are you sure you want to delete ${selectedAccounts.length} selected accounts?`)) {
      return;
    }

    try {
      setError('');
      const deletePromises = selectedAccounts.map(accountId => 
        api.delete(`/api/accounts/${accountId}`)
      );
      
      await Promise.all(deletePromises);
      alert(`Successfully deleted ${selectedAccounts.length} accounts`);
      setSelectedAccounts([]);
      fetchAccounts();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete some accounts');
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const getAccountTypeIcon = (type: string) => {
    switch (type) {
      case 'business': return <BusinessIcon />;
      case 'personal': return <PersonIcon />;
      default: return <AccountIcon />;
    }
  };

  const getAccountTypeColor = (type: string) => {
    switch (type) {
      case 'business': return 'primary';
      case 'personal': return 'secondary';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <Container maxWidth="xl">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl">
      <Box sx={{ my: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" component="h1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            Bank Accounts 🏦
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <Button
              variant="outlined"
              startIcon={<FilterIcon />}
              onClick={() => setShowFilters(!showFilters)}
              size="small"
              color="primary"
            >
              {showFilters ? 'Hide Filters' : 'Show Filters'}
            </Button>
            {selectedAccounts.length > 0 && viewMode === 'table' && (
              <Button
                variant="contained"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={handleBulkDelete}
              >
                Delete Selected ({selectedAccounts.length})
              </Button>
            )}
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(e, newMode) => newMode && setViewMode(newMode)}
              size="small"
            >
              <ToggleButton value="table">
                <ViewListIcon />
              </ToggleButton>
              <ToggleButton value="tile">
                <ViewModuleIcon />
              </ToggleButton>
            </ToggleButtonGroup>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={fetchAccounts}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
            >
              Add Account
            </Button>
            <Button
              variant="outlined"
              component="label"
              startIcon={<AccountIcon />}
            >
              Import Bank Accounts
              <input
                type="file"
                accept=".csv,.xlsx,.xls"
                style={{ display: 'none' }}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    console.log('Bank accounts import file selected:', file.name);
                    // TODO: Implement bank accounts import logic
                  }
                }}
              />
            </Button>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Active Filters Chips */}
        {(accountNameFilter || bankNameFilter || accountTypeFilter) && (
          <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
              Active filters:
            </Typography>
            {accountNameFilter && (
              <Chip
                label={`Account Name: ${accountNameFilter}`}
                onDelete={() => setAccountNameFilter('')}
                deleteIcon={<CloseIcon />}
                size="small"
                color="primary"
                variant="outlined"
              />
            )}
            {bankNameFilter && (
              <Chip
                label={`Bank: ${bankNameFilter}`}
                onDelete={() => setBankNameFilter('')}
                deleteIcon={<CloseIcon />}
                size="small"
                color="primary"
                variant="outlined"
              />
            )}
            {accountTypeFilter && (
              <Chip
                label={`Type: ${accountTypeFilter.charAt(0).toUpperCase() + accountTypeFilter.slice(1)}`}
                onDelete={() => setAccountTypeFilter('')}
                deleteIcon={<CloseIcon />}
                size="small"
                color="primary"
                variant="outlined"
              />
            )}
            <Typography variant="caption" sx={{ ml: 'auto', color: 'text.secondary' }}>
              Showing {filteredAccounts.length} of {viewerAccounts.length} accounts
            </Typography>
          </Box>
        )}

        {/* Filters Section */}
        <Collapse in={showFilters}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Filters
              </Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Search Account Name"
                    placeholder="Account name..."
                    value={accountNameFilter}
                    onChange={(e) => setAccountNameFilter(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Search Bank Name"
                    placeholder="Bank name..."
                    value={bankNameFilter}
                    onChange={(e) => setBankNameFilter(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Account Type</InputLabel>
                    <Select
                      value={accountTypeFilter}
                      label="Account Type"
                      onChange={(e) => setAccountTypeFilter(e.target.value)}
                    >
                      <MenuItem value="">All Types</MenuItem>
                      <MenuItem value="business">Business</MenuItem>
                      <MenuItem value="personal">Personal</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
              {(accountNameFilter || bankNameFilter || accountTypeFilter) && (
                <Box sx={{ mt: 2 }}>
                  <Button
                    size="small"
                    onClick={() => {
                      setAccountNameFilter('');
                      setBankNameFilter('');
                      setAccountTypeFilter('');
                    }}
                  >
                    Clear All Filters
                  </Button>
                </Box>
              )}
            </CardContent>
          </Card>
        </Collapse>

        {viewerAccounts.length === 0 ? (
          <Card>
            <CardContent>
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <AccountIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No bank accounts yet.
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Add your first bank account to get started.
                </Typography>
              </Box>
            </CardContent>
          </Card>
        ) : viewMode === 'table' ? (
          <TableContainer component={Paper} sx={FIT_TABLE_CONTAINER_SX}>
                <Table size="small" sx={FIT_TABLE_SX}>
                  <FitColgroup
                    cols={buildFitColgroup([
                      { key: 'select', pct: 3 },
                      { key: 'accountName' },
                      { key: 'bank', pct: 16 },
                      { key: 'accountNumber', pct: 18 },
                      { key: 'type', pct: 14 },
                      { key: 'balance', pct: 12 },
                      { key: 'actions', pct: 8 },
                    ])}
                  />
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedAccounts.length === filteredAccounts.length && filteredAccounts.length > 0}
                          indeterminate={selectedAccounts.length > 0 && selectedAccounts.length < filteredAccounts.length}
                          onChange={handleSelectAll}
                        />
                      </TableCell>
                      <TableCell>{fitHeaderLabel('Account\nName')}</TableCell>
                      <TableCell>Bank</TableCell>
                      <TableCell>{fitHeaderLabel('Account\nNumber')}</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell align="right">Balance</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredAccounts.map((account) => (
                      <TableRow 
                        key={account.id} 
                        hover
                        selected={selectedAccounts.includes(account.id)}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={selectedAccounts.includes(account.id)}
                            onChange={() => handleSelectOne(account.id)}
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                            <Box component="span" sx={{ display: 'flex', flexShrink: 0 }}>
                              {getAccountTypeIcon(account.account_type)}
                            </Box>
                            <Typography variant="body2" fontWeight="medium" sx={fitTruncateSx} title={account.account_name}>
                              {account.account_name}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={fitTruncateSx} title={account.bank_name}>
                            {account.bank_name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontFamily="monospace" sx={fitTruncateSx} title={account.account_number}>
                            {account.account_number}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={account.account_type.charAt(0).toUpperCase() + account.account_type.slice(1)}
                            size="small"
                            color={getAccountTypeColor(account.account_type) as any}
                            icon={getAccountTypeIcon(account.account_type)}
                            sx={fitChipSx}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight="medium">
                            {formatCurrency(account.current_balance)}
                          </Typography>
                        </TableCell>
                        <TableCell align="center" className="fit-table-no-clip">
                          <Box sx={{ display: 'flex', gap: 0.25, justifyContent: 'center' }}>
                            <Tooltip title="Edit Account">
                              <IconButton
                                size="small"
                                onClick={() => handleOpenDialog(account)}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete Account">
                              <IconButton
                                size="small"
                                onClick={() => handleOpenDeleteDialog(account)}
                                color="error"
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
        ) : (
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 2 }}>
            {filteredAccounts.map((account) => (
              <Card key={account.id} sx={{ height: '100%' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {getAccountTypeIcon(account.account_type)}
                      <Typography variant="h6" component="h3">
                        {account.account_name}
                      </Typography>
                    </Box>
                    <Checkbox
                      checked={selectedAccounts.includes(account.id)}
                      onChange={() => handleSelectOne(account.id)}
                    />
                  </Box>
                  
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Bank
                    </Typography>
                    <Typography variant="body1">
                      {account.bank_name}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Account Number
                    </Typography>
                    <Typography variant="body1" fontFamily="monospace">
                      {account.account_number}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Type
                    </Typography>
                    <Chip
                      label={account.account_type.charAt(0).toUpperCase() + account.account_type.slice(1)}
                      size="small"
                      color={getAccountTypeColor(account.account_type) as any}
                      icon={getAccountTypeIcon(account.account_type)}
                    />
                  </Box>
                  
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Balance
                    </Typography>
                    <Typography variant="h6" color="primary">
                      {formatCurrency(account.current_balance)}
                    </Typography>
                  </Box>
                </CardContent>
                <CardActions sx={{ justifyContent: 'flex-end', pt: 0 }}>
                  <Tooltip title="Edit Account">
                    <IconButton
                      size="small"
                      onClick={() => handleOpenDialog(account)}
                    >
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete Account">
                    <IconButton
                      size="small"
                      onClick={() => handleOpenDeleteDialog(account)}
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </CardActions>
              </Card>
            ))}
          </Box>
        )}

        {/* Add/Edit Account Dialog */}
        <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
          <DialogTitle>
            {selectedAccount ? 'Edit Bank Account' : 'Add New Bank Account'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2 }}>
              <TextField
                fullWidth
                label="Account Name"
                value={formData.account_name}
                onChange={(e) => setFormData({ ...formData, account_name: e.target.value })}
                required
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="Account Number"
                value={formData.account_number}
                onChange={(e) => setFormData({ ...formData, account_number: e.target.value })}
                required
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="Bank Name"
                value={formData.bank_name}
                onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })}
                required
                sx={{ mb: 2 }}
              />
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Account Type</InputLabel>
                <Select
                  value={formData.account_type}
                  label="Account Type"
                  onChange={(e) => setFormData({ ...formData, account_type: e.target.value })}
                >
                  <MenuItem value="business">Business</MenuItem>
                  <MenuItem value="personal">Personal</MenuItem>
                </Select>
              </FormControl>
              <TextField
                fullWidth
                label="Current Balance"
                type="number"
                value={formData.current_balance}
                onChange={(e) => setFormData({ ...formData, current_balance: e.target.value })}
                inputProps={{ step: "0.01" }}
                sx={{ mb: 2 }}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              disabled={!formData.account_name || !formData.account_number || !formData.bank_name}
            >
              {selectedAccount ? 'Update' : 'Create'} Account
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog} maxWidth="sm" fullWidth>
          <DialogTitle>Delete Bank Account</DialogTitle>
          <DialogContent>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Are you sure you want to delete this bank account? This action cannot be undone.
            </Typography>
            {selectedAccount && (
              <Box sx={{ p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                <Typography variant="subtitle2">Account Details:</Typography>
                <Typography variant="body2">
                  <strong>Name:</strong> {selectedAccount.account_name}
                </Typography>
                <Typography variant="body2">
                  <strong>Bank:</strong> {selectedAccount.bank_name}
                </Typography>
                <Typography variant="body2">
                  <strong>Number:</strong> {selectedAccount.account_number}
                </Typography>
                <Typography variant="body2">
                  <strong>Balance:</strong> {formatCurrency(selectedAccount.current_balance)}
                </Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDeleteDialog}>Cancel</Button>
            <Button onClick={handleDelete} variant="contained" color="error">
              Delete Account
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
};
